import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";

import UserActions from "store/actions/users.js";
import useAppInsightsHandler from "hooks/useAppInsightsHandler.js";

import { deleteUser, getRole, getSystems, getUsers } from "api/users.js";

import getLogger from "utils/logger.util.js";
import { getRowFormattedData } from "utils/utils.js";
import { userProfileStrings, USERS_TABLE_STRINGS } from "constants/constantStrings.js";

import AgTable from "components/AgTable";
import BadgeLabel from "components/BadgeLabel";
import CriticalDeleteDialog from "components/CriticalDeleteDialog";
import DupontButton from "components/DupontButton";
import Filter from "components/Filter";
import PageLoading from "components/PageLoading/index.js";
import TableDialog from "components/TableDialog/index.js";

import UserForm from "./UserForm.jsx";
import { DEFAULT_USER_DATA, getUsersTableColConf } from "./Users.table.config.js";

const Users = React.memo(() => {
  const dispatch = useDispatch();
  const { users, roles, systemsList } = useSelector(state => state.usersData);

  const [isLoaded, setIsLoaded] = useState(false);
  const [isUserFormOpen, setIsUserFormOpen] = useState(false);
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false);
  const [user, setUser] = useState();
  const [popupData, setPopupData] = useState();
  const [formType, setFormType] = useState("");
  const [isRerendering, setIsRerendering] = useState(false);
  const [deleteUserData, setDeleteUserData] = useState({});
  const [filterValue, setFilterValue] = useState("");
  const [userBadgeCount, setUserBadgeCount] = useState();
  const [showTableFilterIcon, setShowTableFilterIcon] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const objAppInsights = useAppInsightsHandler();

  useEffect(() => {
    if (!isLoaded || isRerendering) {
      const fetchData = async () => {
        try {
          const response = await getUsers();
          if (response && response.data && response.status === 200) {
            const { data } = response;
            dispatch(UserActions.setUsers(data));
            setUserBadgeCount(data.length);
          }
        } catch (err) {
          getLogger('Users').error('fetchData - error: ', err);
        } finally {
          setIsLoaded(true);
          setIsRerendering(false);
        }
      };
      fetchData();
    }
  }, [isRerendering]); // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    setUserBadgeCount(users?.length || 0);
  }, [users]);

  const fetchRole = async () => {
    if (roles.length === 0) {
      const response = await getRole();
      if (response && response.data && response.status === 200) {
        dispatch(UserActions.setUserRoles(response.data));
      }
    }
  };

  const fetchSystems = async () => {
    const response = await getSystems();
    if (response && response.data && response.status === 200) {
      const systems = { All: "All", ...Object.fromEntries(
        Object.entries(response.data).sort()
      )};
      dispatch(UserActions.setSystemsList(systems));
    }
  };

  const openUserForm = (type, userData) => {
    setUser(userData);
    fetchRole();
    fetchSystems();
    setFormType(type);
    setIsUserFormOpen(true);
  };

  const onUserFormClose = userData => {
    setIsUserFormOpen(false);
    if (userData) {
      if (formType === "Edit") {
        dispatch(UserActions.editUserAction(userData));
      } else {
        dispatch(UserActions.addUserAction(userData));
      }
    }
  };

  const handleOpenUserForm = () => {
    objAppInsights.trackEvent("addUserClick");
    openUserForm("Add", DEFAULT_USER_DATA);
  };
  const handleEditClick = userData => {
    objAppInsights.trackEvent("editUserClick");
    openUserForm("Edit", userData);
  };
  const getDeleteDlgTitle = () => userProfileStrings.deleteUser.replace("[%]", deleteUserData.Name);

  const handleTrashClick = rowData => {
    objAppInsights.trackEvent("deleteUserClick");
    const { UserId, Name } = rowData;
    setDeleteUserData({ UserId, Name });
    setIsDeleteUserModalOpen(true);
  };

  const onDeleteDlgClose = async data => {
    if (data.action === "CONFIRM") {
      submitDeleteUser(data.deleteReason);
    } else {
      setIsDeleteUserModalOpen(false);
    }
  };

  const submitDeleteUser = async Reason => {
    const { UserId } = deleteUserData;
    const payload = { UserId, Reason };
    try {
      setIsDeleting(true);
      await deleteUser(payload);
      dispatch(UserActions.deleteUserAction(UserId));
      objAppInsights.trackEvent("userDeleted");
    } catch (err) {
      getLogger('Users').error("Delete User Error: ", err);
    } finally {
      setIsDeleteUserModalOpen(false);
      setIsDeleting(false);
    }
  };

  const UserTablesColumnsConfig = getUsersTableColConf(showTableFilterIcon);

  const closePopup = () => setPopupData(null);
  const onRowClicked = ({ data }) => {
    objAppInsights.trackEvent("usersRowClick");
    const rowData = getRowFormattedData(UserTablesColumnsConfig, data);
    const title = data.Name;
    const subTitle = USERS_TABLE_STRINGS.viewUser;
    setPopupData({ title, subTitle, rowData });
  };

  return (
    <div className="setting-page-padding">
      {!isLoaded ? (
        <PageLoading />
      ) : (
        <>
          <div className="flex-between-center">
            <BadgeLabel badgeDescription={userProfileStrings.users} badgeText={userBadgeCount} />
            <div>
              <DupontButton id="add_user_btn" onClick={handleOpenUserForm}>{userProfileStrings.addUser}</DupontButton>
            </div>
          </div>
          <div className="flex-between-center">
            <div className="descriptionText">{userProfileStrings.userdetail}</div>
            <div className="autoWidth">
              <Filter
                data={users}
                setFilterValue={setFilterValue}
                showfilter={false}
                setShowTableFilterIcon={setShowTableFilterIcon}
                showTableFilterIcon={showTableFilterIcon}
              />
            </div>
          </div>
          <AgTable
            rowData={users}
            onRowClicked={onRowClicked}
            quickFilterText={filterValue}
            columnDefs={UserTablesColumnsConfig}
            context={{ handleTrashClick, handleEditClick }}
            setBadgeCount={setUserBadgeCount}
          />

          {isUserFormOpen && (
            <UserForm
              type={formType}
              usersData={users}
              user={user}
              setUser={setUser}
              systemsList={systemsList}
              roles={roles}
              onUserFormClose={onUserFormClose}
            />
          )}

          {isDeleteUserModalOpen && (
            <CriticalDeleteDialog
              title={getDeleteDlgTitle()}
              description={userProfileStrings.deleteUserWarning}
              onDialogClose={onDeleteDlgClose}
              isOverlayLoader={isDeleting}
            />
          )}
          {popupData && <TableDialog popupData={popupData} closePopup={closePopup} colWidth="user-table-dialog-col-width" />}
        </>
      )}
    </div>
  );
});

export default Users;
